import './App.css';
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './utils/toast';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ComparisonProvider } from './contexts/ComparisonContext';
import LuxuryHeader from './components/LuxuryHeader';
import MobileBottomNav from './components/MobileBottomNav';
import LazyCookieConsent from './components/LazyCookieConsent';
import IdleMount from './components/IdleMount';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import lazyWithRetry from './utils/lazyWithRetry';
import { StoreFactsProvider } from './contexts/StoreFactsContext';
import LazyTrackers from './components/LazyTrackers';
import ChromeGate from './components/ChromeGate';
import NetworkStatusGate from './components/network/NetworkStatusGate';
import ExperimentsBoot from './components/ExperimentsBoot';

// Non-critical chrome: separate chunks, mounted when the browser is idle after
// `load` (IdleMount) so neither their code nor their API calls compete with
// the first paint.
const EmailCapturePopup = lazyWithRetry(() => import('./components/EmailCapturePopup'));
const PWAInstallPrompt  = lazyWithRetry(() => import('./components/PWAInstallPrompt'));
const AIStyleAssistant  = lazyWithRetry(() => import('./components/AIStyleAssistant'));
const WhatsAppButton    = lazyWithRetry(() => import('./components/WhatsAppButton'));
const BackToTop         = lazyWithRetry(() => import('./components/BackToTop'));
// The slim checkout header is only needed on the enclosed checkout routes.
const CheckoutHeader    = lazyWithRetry(() => import('./components/checkout/CheckoutHeader'));
// Below the fold on every page: its own chunk (requested with the page's
// chunk). The slot keeps the footer's box (same content-visibility class,
// hence the same reserved height), so nothing moves when it arrives.
const Footer            = lazyWithRetry(() => import('./components/Footer'));
const FooterSlot = () => <div className="anum-cv-footer mt-section" aria-hidden="true" data-testid="footer-slot" />;

// ── Route-level code splitting (React.lazy) ──
// Eagerly load the Home page for instant first paint; lazy-load the rest.
import Home from './pages/Home';
const LuxuryProducts   = lazyWithRetry(() => import('./pages/LuxuryProducts'));
const ProductDetail    = lazyWithRetry(() => import('./pages/ProductDetail'));
const Cart             = lazyWithRetry(() => import('./pages/Cart'));
const EnhancedCheckout = lazyWithRetry(() => import('./pages/EnhancedCheckout'));
const PaymentSuccess   = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const Account          = lazyWithRetry(() => import('./pages/Account'));
const Orders           = lazyWithRetry(() => import('./pages/Orders'));
const OrderDetail      = lazyWithRetry(() => import('./pages/OrderDetail'));
const ReturnRequest    = lazyWithRetry(() => import('./pages/ReturnRequest'));
const MyReturns        = lazyWithRetry(() => import('./pages/MyReturns'));
const WishlistPage     = lazyWithRetry(() => import('./pages/WishlistPage'));
const Stylist          = lazyWithRetry(() => import('./pages/Stylist'));
const TryOn            = lazyWithRetry(() => import('./pages/TryOn'));
const AboutEnhanced    = lazyWithRetry(() => import('./pages/AboutEnhanced'));
const ContactEnhanced  = lazyWithRetry(() => import('./pages/ContactEnhanced'));
const FAQ              = lazyWithRetry(() => import('./pages/FAQ'));
const InfoPage         = lazyWithRetry(() => import('./pages/InfoPage'));
const PrivacyPolicy    = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService   = lazyWithRetry(() => import('./pages/TermsOfService'));
const ShippingPolicy   = lazyWithRetry(() => import('./pages/ShippingPolicy'));
const ReturnsPolicy    = lazyWithRetry(() => import('./pages/ReturnsPolicy'));
const GiftCards        = lazyWithRetry(() => import('./pages/GiftCards'));
const Wholesale        = lazyWithRetry(() => import('./pages/Wholesale'));
const Blog             = lazyWithRetry(() => import('./pages/Blog'));
const VIPPage          = lazyWithRetry(() => import('./pages/VIPPage'));
const NotFound         = lazyWithRetry(() => import('./pages/NotFound'));
const DeleteAccount    = lazyWithRetry(() => import('./pages/DeleteAccount'));
const Unsubscribe      = lazyWithRetry(() => import('./pages/Unsubscribe'));
const PersonalShopping = lazyWithRetry(() => import('./pages/PersonalShopping'));
const PreOrder         = lazyWithRetry(() => import('./pages/PreOrder'));
const CorporateGifting = lazyWithRetry(() => import('./pages/CorporateGifting'));
const BagPrefill       = lazyWithRetry(() => import('./pages/BagPrefill'));
const ShopWithAI       = lazyWithRetry(() => import('./pages/ShopWithAI'));

// Admin (separate chunk: routes, AdminProvider and every admin page)
const AdminApp = lazyWithRetry(() => import('./pages/admin/AdminApp'));
const Brands           = lazyWithRetry(() => import('./pages/Brands'));
const BrandDetail      = lazyWithRetry(() => import('./pages/BrandDetail'));
const Collections      = lazyWithRetry(() => import('./pages/Collections'));
const CollectionDetail = lazyWithRetry(() => import('./pages/CollectionDetail'));
const Guides           = lazyWithRetry(() => import('./pages/Guides'));
const GuideDetail      = lazyWithRetry(() => import('./pages/GuideDetail'));

// Route fallback: a thin gold hairline (maison: no spinners where a quiet bar will do).
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
    <span className="sr-only">Loading</span>
    <div className="relative h-px w-28 overflow-hidden bg-crimson/20" aria-hidden="true">
      <div className="absolute inset-y-0 w-1/2 animate-pulse bg-crimson" />
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <StoreFactsProvider>
          <ComparisonProvider>
                  {/* Organization / OnlineStore / WebSite JSON-LD lives in public/index.html
                      so non-JS crawlers see it; per-page schema comes from SEOHead. */}
                  <Routes>
                  {/* Customer Routes */}
                  <Route path="/*" element={
                    <div className="App app-shell flex flex-col min-h-screen text-charcoal dark:text-white">
                      {/* "You're offline. Showing saved pages." (lazy band, only once the connection drops). */}
                      <RouteErrorBoundary silent><NetworkStatusGate /></RouteErrorBoundary>
                      {/* Checkout is enclosed: slim header, no store chrome (utils/chromeRoutes). */}
                      <ChromeGate minimal={<Suspense fallback={<div className="min-h-[73px]" aria-hidden="true" />}><CheckoutHeader /></Suspense>}>
                        <LuxuryHeader />
                      </ChromeGate>
                      <main id="main-content" className="flex-1">
                        {/* A page that cannot load (offline chunk, render error) shows an
                            in-page message; header and footer stay (RouteErrorBoundary). */}
                        <RouteErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/products" element={<LuxuryProducts />} />
                          <Route path="/products/:id" element={<ProductDetail />} />
                          <Route path="/cart" element={<Cart />} />
                          {/* AI assistants: signed bag links + how to connect (docs/AGENTIC_COMMERCE.md) */}
                          <Route path="/bag/prefill" element={<BagPrefill />} />
                          <Route path="/ai" element={<ShopWithAI />} />
                          <Route path="/checkout" element={<EnhancedCheckout />} />
                          <Route path="/payment-success" element={<PaymentSuccess />} />
                          <Route path="/account" element={<Account />} />
                          <Route path="/orders" element={<Orders />} />
                          <Route path="/orders/:id" element={<OrderDetail />} />
                          <Route path="/orders/:id/return" element={<ReturnRequest />} />
                          <Route path="/my-returns" element={<MyReturns />} />
                          <Route path="/wishlist" element={<WishlistPage />} />
                          <Route path="/stylist" element={<Stylist />} />
                          <Route path="/stylist/try-on/:id" element={<TryOn />} />
                          <Route path="/about" element={<AboutEnhanced />} />
                          <Route path="/contact" element={<ContactEnhanced />} />
                          <Route path="/faq" element={<FAQ />} />

                          {/* Canonical policy URLs + redirects for duplicates (SEO) */}
                          <Route path="/privacy" element={<PrivacyPolicy />} />
                          <Route path="/terms" element={<TermsOfService />} />
                          <Route path="/shipping-policy" element={<ShippingPolicy />} />
                          <Route path="/returns" element={<ReturnsPolicy />} />
                          <Route path="/delete-account" element={<DeleteAccount />} />
                          <Route path="/unsubscribe" element={<Unsubscribe />} />
                          <Route path="/privacy-policy"   element={<Navigate to="/privacy" replace />} />
                          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
                          <Route path="/return-policy"    element={<Navigate to="/returns" replace />} />
                          <Route path="/shipping"         element={<Navigate to="/shipping-policy" replace />} />
                          <Route path="/info/privacy"     element={<Navigate to="/privacy" replace />} />
                          <Route path="/info/terms"       element={<Navigate to="/terms" replace />} />
                          <Route path="/info/shipping"    element={<Navigate to="/shipping-policy" replace />} />
                          <Route path="/info/returns"     element={<Navigate to="/returns" replace />} />

                          {/* Category aliases */}
                          <Route path="/women" element={<Navigate to="/products?cat=women" replace />} />
                          <Route path="/men" element={<Navigate to="/products?cat=men" replace />} />
                          <Route path="/bags" element={<Navigate to="/products?cat=bags" replace />} />
                          <Route path="/jewelry" element={<Navigate to="/products?cat=jewelry" replace />} />
                          <Route path="/perfumes" element={<Navigate to="/products?cat=perfumes" replace />} />
                          <Route path="/accessories" element={<Navigate to="/products?cat=accessories" replace />} />
                          <Route path="/sale" element={<Navigate to="/products?sale=true" replace />} />
                          <Route path="/shop" element={<Navigate to="/products" replace />} />
                          {/* Arabic URL slugs (UTF-8; were mojibake and never matched) */}
                          <Route path="/حقائب" element={<Navigate to="/products?cat=bags" replace />} />
                          <Route path="/مجوهرات" element={<Navigate to="/products?cat=jewelry" replace />} />
                          <Route path="/عطور" element={<Navigate to="/products?cat=perfumes" replace />} />
                          <Route path="/نساء" element={<Navigate to="/products?cat=women" replace />} />
                          <Route path="/رجال" element={<Navigate to="/products?cat=men" replace />} />
                          <Route path="/اكسسوارات" element={<Navigate to="/products?cat=accessories" replace />} />
                          <Route path="/تخفيضات" element={<Navigate to="/products?sale=true" replace />} />

                          <Route path="/gift-cards" element={<GiftCards />} />
                          <Route path="/wholesale" element={<Wholesale />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/vip" element={<VIPPage />} />
                          <Route path="/personal-shopping" element={<PersonalShopping />} />
                          <Route path="/pre-order" element={<PreOrder />} />
                          <Route path="/corporate-gifting" element={<CorporateGifting />} />
                          <Route path="/brands" element={<Brands />} />
                          <Route path="/brands/:slug" element={<BrandDetail />} />
                          <Route path="/collections" element={<Collections />} />
                          <Route path="/collections/:slug" element={<CollectionDetail />} />
                          {/* Buying guides (backend data/content/guides) */}
                          <Route path="/guides" element={<Guides />} />
                          <Route path="/guides/:slug" element={<GuideDetail />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </Suspense>
                        </RouteErrorBoundary>
                      </main>
                      {/* Phones: the footer keeps its own room for the bottom nav, so the
                          96px spacer is only for the enclosed (checkout) pages. */}
                      <ChromeGate minimal={<div className="h-24 shrink-0 md:hidden" aria-hidden="true" />}>
                        <RouteErrorBoundary silent><Suspense fallback={<FooterSlot />}><Footer /></Suspense></RouteErrorBoundary>
                        <IdleMount>
                          <RouteErrorBoundary silent>
                          <Suspense fallback={null}>
                            <WhatsAppButton />
                            <BackToTop />
                          </Suspense>
                          </RouteErrorBoundary>
                        </IdleMount>
                        <MobileBottomNav
                          onWishlistOpen={() => window.dispatchEvent(new CustomEvent('anum:open-wishlist'))}
                        />
                      </ChromeGate>
                      <LazyCookieConsent />
                      {/* A/B test assignments: after load + idle, never before the first paint. */}
                      <IdleMount><ExperimentsBoot /></IdleMount>
                    </div>
                  } />

                  {/* Admin: its own lazy chunk with AdminProvider (pages/admin/AdminApp.js),
                      so no admin code or admin session logic ships in main.js. */}
                  <Route path="/admin/*" element={
                    <Suspense fallback={<PageLoader />}><AdminApp /></Suspense>
                  } />
                </Routes>
                {/* ST3: ink toasts, ivory 14 px text; a 2 px gold start edge for
                    success and a sale-red one for errors (index.css). */}
                <Toaster
                  position="top-right"
                  dir="auto"
                  toastOptions={{ classNames: { toast: 'atelier-toast', success: 'atelier-toast-success', error: 'atelier-toast-error' } }}
                />
                <LazyTrackers />
                <ChromeGate>
                  <IdleMount>
                    <RouteErrorBoundary silent>
                    <Suspense fallback={null}>
                      <EmailCapturePopup />
                      <AIStyleAssistant />
                      <PWAInstallPrompt />
                    </Suspense>
                    </RouteErrorBoundary>
                  </IdleMount>
                </ChromeGate>
            </ComparisonProvider>
          </StoreFactsProvider>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  </BrowserRouter>
    </ErrorBoundary>
);
}

export default App;

